import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, parseCents, type PriceBookItem } from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { PageFooter } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  SearchField,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

const KIND_TONE = {
  labor: 'blue',
  material: 'violet',
  fee: 'amber',
  discount: 'green',
} as const;

export function PriceBookPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<PriceBookItem | 'new' | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: keys.priceBook(q),
    queryFn: () => api.priceBook.list(q || undefined),
    select: (d) => d.items,
    placeholderData: (prev) => prev,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.priceBook.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pricebook'] }),
  });

  return (
    <>
      <PageHeader
        eyebrow="Business"
        title="Price book"
        subtitle="What the office and the field both pull line items from"
        actions={
          <>
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Search the price book"
              className="w-[260px]"
            />
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              New item
            </Button>
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <Card
          title="Items"
          icon="book"
          count={items.length}
          divided
          bodyClassName="p-0"
          className="max-w-[1080px]"
        >
          {isLoading ? (
            <div className="grid place-items-center py-16">
              <Spinner className="size-5 text-ink-4" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="Nothing in the price book yet" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th className="w-[86px]">Code</Th>
                  <Th>Item</Th>
                  <Th className="w-[100px]">Type</Th>
                  <Th align="center" className="w-[64px]">Tax</Th>
                  <Th align="right" className="w-[110px]">Price</Th>
                  <Th className="w-[110px]" />
                </tr>
              </thead>
              <tbody>
                {items.map((item: PriceBookItem) => (
                  <tr key={item.id} className="hover:bg-raised">
                    <Td className="font-mono text-[12px] text-ink-4">{item.code ?? '—'}</Td>
                    <Td>
                      <div className="text-[14px] font-semibold text-ink">{item.name}</div>
                      {item.description && (
                        <div className="mt-1 text-[12px] text-ink-4">{item.description}</div>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={KIND_TONE[item.kind]}>{item.kind}</Badge>
                    </Td>
                    <Td align="center" className="text-ink-3">
                      {item.taxable ? 'Yes' : 'No'}
                    </Td>
                    <Td align="right" className="tnum font-medium">
                      {formatCents(item.unitPriceCents)}
                      {item.unit && <span className="text-ink-4">/{item.unit}</span>}
                    </Td>
                    <Td align="right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(item)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove.mutate(item.id)}
                          title="Archive — past invoices keep referencing it"
                        >
                          Archive
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <PageFooter label={`${items.length} items`} />
      </div>

      <ItemModal
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void qc.invalidateQueries({ queryKey: ['pricebook'] });
        }}
      />
    </>
  );
}

function ItemModal({
  item,
  onClose,
  onSaved,
}: {
  item: PriceBookItem | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = item !== 'new' && item !== null ? item : null;
  const [form, setForm] = useState(() => ({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    description: existing?.description ?? '',
    kind: existing?.kind ?? ('labor' as PriceBookItem['kind']),
    price: ((existing?.unitPriceCents ?? 0) / 100).toFixed(2),
    unit: existing?.unit ?? '',
    taxable: existing?.taxable ?? true,
  }));

  // Reset the form whenever a different item is opened.
  const [key, setKey] = useState(existing?.id ?? 'new');
  if ((existing?.id ?? 'new') !== key) {
    setKey(existing?.id ?? 'new');
    setForm({
      code: existing?.code ?? '',
      name: existing?.name ?? '',
      description: existing?.description ?? '',
      kind: existing?.kind ?? 'labor',
      price: ((existing?.unitPriceCents ?? 0) / 100).toFixed(2),
      unit: existing?.unit ?? '',
      taxable: existing?.taxable ?? true,
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code.trim() || null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        kind: form.kind,
        unitPriceCents: parseCents(form.price) ?? 0,
        unit: form.unit.trim() || null,
        taxable: form.taxable,
      };
      return existing
        ? api.priceBook.update(existing.id, payload)
        : api.priceBook.create(payload);
    },
    onSuccess: onSaved,
  });

  return (
    <Modal
      open={item !== null}
      onClose={onClose}
      title={existing ? 'Edit item' : 'New price book item'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-[110px_1fr] gap-3">
          <Field label="Code">
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="font-mono"
            />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
        </div>
        <Field label="Description">
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Type">
            <Select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as PriceBookItem['kind'] })}
            >
              <option value="labor">Labour</option>
              <option value="material">Material</option>
              <option value="fee">Fee</option>
              <option value="discount">Discount</option>
            </Select>
          </Field>
          <Field label="Price">
            <Input
              value={form.price}
              inputMode="decimal"
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="text-right tnum"
            />
          </Field>
          <Field label="Unit" hint="hr, each">
            <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          </Field>
          <Field label="Taxable">
            <label className="flex h-9 items-center gap-2 text-[13.5px]">
              <input
                type="checkbox"
                checked={form.taxable}
                onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
                className="size-4 accent-accent"
              />
              Charge sales tax
            </label>
          </Field>
        </div>
      </div>
    </Modal>
  );
}
